/**
 * User Context
 *
 * Provides a single user model across the app.
 * - If VITE_AUTH_BYPASS is true, uses dummy user data.
 * - Otherwise, maps backend/auth user info into the shared UserType model.
 */

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react"

import type { ReactNode } from "react"

import { env } from "@/shared/config/env"
import { userProfileService } from "@/shared/users/api"
import { userData } from "@/shared/mocks/user"
import type { UserType } from "@/types"
import { useAuth } from "@/contexts/auth-context"

type UserContextValue = {
  user: UserType | null
  isLoading: boolean
  hasAccess: boolean
  accessChecked: boolean
  roles: string[]
  orgType: string[]
}

const UserContext = createContext<UserContextValue | undefined>(undefined)

const EMPTY_USER: UserType = {
  id: "",
  firstName: "",
  lastName: "",
  name: "",
  password: "",
  username: "",
  role: "",
  avatar: "",
  background: "",
  status: "",
  phoneNumber: "",
  email: "",
  state: "",
  country: "",
  address: "",
  zipCode: "",
  language: "",
  timeZone: "",
  currency: "",
  organization: "",
  twoFactorAuth: false,
  loginAlerts: false,
  accountReoveryOption: "email",
  connections: 0,
  followers: 0,
}

const normalizeUser = (input: Partial<UserType> | null | undefined): UserType => {
  if (!input) return EMPTY_USER
  const firstName = input.firstName || ""
  const lastName = input.lastName || ""
  const name = input.name || `${firstName} ${lastName}`.trim()
  return {
    ...EMPTY_USER,
    ...input,
    name: name || EMPTY_USER.name,
  }
}

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value)
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number.parseInt(value.trim(), 10)
  }
  return null
}

const resolveSicilNo = (input: Partial<UserType> | null | undefined): number | null => {
  if (!input) return null
  return (
    parseNumeric(input.sicilNo) ??
    parseNumeric(input.id) ??
    parseNumeric(input.username) ??
    null
  )
}

const mergeAuthUser = (prev: UserType | null, authUser: Partial<UserType>): UserType => {
  const resolvedSicilNo = resolveSicilNo(authUser) ?? prev?.sicilNo ?? null

  return normalizeUser({
    ...(prev ?? {}),
    id: authUser.id || prev?.id || "",
    sicilNo: resolvedSicilNo,
    email: authUser.email || prev?.email || "",
    name: authUser.name || prev?.name || "",
    avatar: authUser.avatar || prev?.avatar || "",
    status: authUser.status || prev?.status || "",
    username: prev?.username || (resolvedSicilNo ? String(resolvedSicilNo) : ""),
  })
}

export function UserProvider({ children }: { children: ReactNode }) {
  const auth = useAuth()
  const [user, setUser] = useState<UserType | null>(
    env.VITE_AUTH_BYPASS ? userData : null
  )
  const [isLoading, setIsLoading] = useState(!env.VITE_AUTH_BYPASS)
  const [roles, setRoles] = useState<string[]>(env.VITE_AUTH_BYPASS ? ["USER"] : [])
  const [orgType, setOrgType] = useState<string[]>([])
  const [accessChecked, setAccessChecked] = useState(env.VITE_AUTH_BYPASS)

  useEffect(() => {
    if (env.VITE_AUTH_BYPASS) {
      setUser(userData)
      setIsLoading(false)
      return
    }

    if (!auth.user) {
      if (auth.isLoading) {
        setIsLoading(true)
        return
      }
      setUser(null)
      setIsLoading(false)
      return
    }

    setUser((prev) => mergeAuthUser(prev, auth.user as Partial<UserType>))
    setIsLoading(false)
  }, [auth.user, auth.isLoading])

  const getUserCalledRef = useRef(false)

  useEffect(() => {
    if (env.VITE_AUTH_BYPASS) return
    if (!auth.isAuthenticated || !auth.token) return
    if (getUserCalledRef.current) return
    getUserCalledRef.current = true

    let isCancelled = false
    setAccessChecked(false)

    userProfileService
      .getCurrentUser()
      .then((profile) => {
        if (!profile || isCancelled) {
          if (!isCancelled) {
            setRoles([])
            setOrgType([])
            setAccessChecked(true)
          }
          return
        }

        setRoles(profile.loginUserRoles)
        setOrgType(profile.orgType)
        setUser((prev) => {
          const base = prev ?? EMPTY_USER
          const fullName = `${profile.ad} ${profile.soyad}`.trim()
          return normalizeUser({
            ...base,
            sicilNo: profile.sicilNo,
            firstName: profile.ad || base.firstName,
            lastName: profile.soyad || base.lastName,
            name: fullName || base.name,
            username: base.username || String(profile.sicilNo),
            phoneNumber: profile.mobilTel || base.phoneNumber,
            birimKod: profile.birimKod || base.birimKod,
            organization: profile.birimKod || base.organization,
            kurumKod: profile.kurumKodGorev || base.kurumKod,
            avatar: profile.resimDataUrl || profile.resimUrl || base.avatar,
          })
        })
        setAccessChecked(true)
      })
      .catch(() => {
        if (!isCancelled) {
          setRoles([])
          setOrgType([])
          setAccessChecked(true)
        }
      })

    return () => {
      isCancelled = true
    }
  // Only re-run when authentication status changes, not on every token refresh
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated])

  const hasAccess = roles.length > 0

  const value = useMemo(
    () => ({
      user,
      isLoading,
      hasAccess,
      accessChecked,
      roles,
      orgType,
    }),
    [user, isLoading, hasAccess, accessChecked, roles, orgType]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error("useUser must be used within a UserProvider")
  }
  return context
}
